import React, { useState, useEffect, useRef } from 'react';
import { Toggle } from '@/components/ui/toggle';
import { Search, Tag, X } from 'lucide-react';
import { availableTags } from '@/config/references';
import { useTranslation } from '@/hooks/useTranslation';

interface ReferencesFilterProps {
  selectedTags: string[];
  onTagToggle: (tag: string) => void;
  onClearFilters: () => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
}

const ReferencesFilter = ({ 
  selectedTags, 
  onTagToggle, 
  onClearFilters, 
  searchTerm = '', 
  onSearchChange 
}: ReferencesFilterProps) => {
  const { t } = useTranslation();
  // Verwende die vordefinierten Tags aus der Konfigurationsdatei
  const allTags = availableTags;
  
  // State für die Sichtbarkeit des Suchfelds
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Event-Handler für die Tastenkombination (Strg+F)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prüfe auf Strg+F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault(); // Verhindere das Standard-Suchverhalten des Browsers
        setIsSearchVisible(true);
        
        // Fokussiere das Suchfeld, wenn es sichtbar wird
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 10);
      }
      
      // Schließe die Suche mit Escape
      if (e.key === 'Escape' && isSearchVisible) {
        setIsSearchVisible(false);
        onSearchChange?.(''); // Leere die Suche beim Schließen
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchVisible, onSearchChange]);
  
  // Handler für Änderungen im Suchfeld
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange?.(e.target.value);
  };
  
  // Schließe die Suche und setze sie zurück
  const closeSearch = () => {
    setIsSearchVisible(false);
    onSearchChange?.('');
  };

  return (
    <section className="py-6 bg-gray-50 border-b border-gray-200">
      <div className="container">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag size={20} className="text-blue-700" />
              <h2 className="text-lg font-medium text-gray-900">Nach Leistungsbereich filtern</h2>
              
              {/* Versteckter Hinweis auf die Tastenkombination */}
              <div className="hidden md:flex items-center ml-4 text-xs text-gray-500">
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-700 font-mono">Strg</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-700 font-mono">F</kbd>
                <span className="ml-1">{t('referencesPage.search.shortcut')}</span>
              </div>
            </div>
            {selectedTags.length > 0 && (
              <button 
                onClick={onClearFilters}
                className="text-sm text-blue-700 hover:text-blue-800 font-medium"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
          
          {/* Suchfeld (versteckt/sichtbar) */}
          {isSearchVisible && (
            <div className="relative mb-4 animate-fade-down animate-duration-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={t('referencesPage.search.placeholder')}
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button 
                    onClick={() => onSearchChange?.('')}
                    className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
                <button 
                  onClick={closeSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <Toggle
                key={tag}
                pressed={selectedTags.includes(tag)}
                onPressedChange={() => onTagToggle(tag)}
                variant="outline"
                size="sm"
                className={`${
                  selectedTags.includes(tag)
                    ? 'bg-blue-100 text-blue-800 border-blue-300' 
                    : 'bg-white border-gray-300'
                } hover:bg-blue-50`}
              >
                {tag}
              </Toggle>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferencesFilter;
